import { add, filter, map, some } from "@prismicio/editor-support/Set"
import clsx from "clsx"
import { useRef } from "react"

import { theme } from "../../theme"
import { Badge } from "../Badge"
import { Box } from "../Box"
import { Button } from "../Button"
import { Icon, type IconName } from "../Icon"
import { Skeleton } from "../Skeleton"
import { Text } from "../Text"
import styles from "./FilterField.module.css"
import { type FilterOption, useFilterField } from "./useFilterField"

export interface FilterFieldProps extends Omit<FooterProps, "searchText"> {
  open: boolean
  setOpen: (open: boolean) => void
  icon: IconName
}

export function FilterField(props: FilterFieldProps) {
  const {
    open,
    setOpen,
    label,
    icon,
    selectedValues,
    fixedSelectedValues = new Set(),
    onSelectedValuesChange,
    ...footerProps
  } = props

  const fieldRef = useRef<HTMLDivElement>(null)

  const {
    deleteItemFromValue,
    onBackspace,
    onKeyDown,
    setSearchText,
    searchText,
    isFieldLocked,
    inputPlaceholder,
  } = useFilterField({
    fieldRef,
    onSelectedValuesChange,
    selectedValues,
    setOpen,
    fixedSelectedValues,
  })

  return (
    <div
      role="button"
      className={clsx(
        styles.filterField,
        styles[`shadow-${open ? "open" : "closed"}`],
      )}
      ref={fieldRef}
      onKeyDown={onKeyDown}
      tabIndex={0}
    >
      <div className={styles.inputContainer}>
        <div className={styles.labelContainer}>
          <Icon name={icon} size="medium" color="grey11" />
          <Text variant="emphasized" color="grey11">
            {label}
          </Text>
        </div>
        <div className={styles.tagsContainer}>
          {map(fixedSelectedValues, (tag) => (
            <Badge
              title={tag.label}
              key={tag.id}
              maxWidth={150}
              size="medium"
              selected
              icon="lock"
            />
          ))}
          {map(selectedValues, (tag) => (
            <Badge
              title={tag.label}
              key={tag.id}
              color="purple"
              maxWidth={150}
              size="medium"
              onClose={() => deleteItemFromValue(tag)}
              selected
            />
          ))}
          <input
            className={styles.input}
            style={{
              color: theme.color[open ? "grey12" : "grey11"],
              cursor: theme.cursor[isFieldLocked ? "notAllowed" : "text"],
            }}
            disabled={isFieldLocked}
            inputMode="text"
            onFocus={() => setOpen(true)}
            placeholder={inputPlaceholder}
            onKeyDown={(event) => {
              if (event.key === "Backspace") onBackspace()
            }}
            value={searchText}
            onChange={(evt) => setSearchText(evt.currentTarget.value)}
          />
        </div>
      </div>
      {open && (
        <div className={styles.footerWrapper}>
          <Footer
            label={label}
            onSelectedValuesChange={onSelectedValuesChange}
            searchText={searchText}
            fixedSelectedValues={fixedSelectedValues}
            selectedValues={selectedValues}
            {...footerProps}
          />
        </div>
      )}
    </div>
  )
}
function filterOptions(
  values: Set<FilterOption>,
  selectedValues: Set<FilterOption>,
  searchTerm: string,
) {
  function optionIsNotSelected(option: FilterOption) {
    return !some(selectedValues, (selectValue) => selectValue.id === option.id)
  }

  function optionMatchTextSearch(option: FilterOption, searchTerm: string) {
    return option.label.toLowerCase().includes(searchTerm.toLowerCase())
  }

  return filter(
    values,
    (option) =>
      optionIsNotSelected(option) && optionMatchTextSearch(option, searchTerm),
  )
}

interface FooterProps extends ErrorFooterProps {
  searchText: string
  selectedValues: Set<FilterOption>
  values: Set<FilterOption>
  fixedSelectedValues?: Set<FilterOption>
  onSelectedValuesChange: (selectedValues: Set<FilterOption>) => void
  mode?: "loading" | "error"
  /** Allows more than 1 value to be selected. Default: true */
  multiple?: boolean
}

function Footer(props: FooterProps) {
  const {
    label,
    searchText,
    values,
    selectedValues,
    fixedSelectedValues = new Set(),
    onSelectedValuesChange,
    mode,
    onErrorRetry,
    multiple = true,
  } = props

  function addItemToValue(valueToAdd: FilterOption) {
    if (!multiple && selectedValues.size > 0) return

    return onSelectedValuesChange(add(selectedValues, valueToAdd))
  }

  if (mode === "loading") return <LoadingFooter />
  if (mode === "error")
    return <ErrorFooter label={label} onErrorRetry={onErrorRetry} />

  return (
    <div className={styles.footer}>
      <Text color="grey11" variant="emphasized">
        Choose {label}
      </Text>
      <div className={styles.optionsContainer}>
        {map(
          filterOptions(
            values,
            new Set([...selectedValues, ...fixedSelectedValues]),
            searchText,
          ),
          (option) => (
            <Badge
              title={option.label}
              key={option.id}
              onClick={() => addItemToValue(option)}
              size="medium"
              maxWidth={150}
            />
          ),
        )}
      </div>
    </div>
  )
}

function LoadingFooter() {
  return (
    <div className={styles.footer}>
      <Skeleton height={24} width={80} borderRadius={6} />
      <Skeleton height={24} width={60} borderRadius={6} />
    </div>
  )
}

interface ErrorFooterProps {
  label: string
  onErrorRetry?: () => void
}

function ErrorFooter(props: ErrorFooterProps) {
  const { label, onErrorRetry } = props

  return (
    <div className={styles.footer}>
      <Box justifyContent="space-between">
        <Box gap={8}>
          <Icon name="alert" size="large" color="tomato11" />
          <Text color="tomato11">Failed to load {label}</Text>
        </Box>
        {onErrorRetry && (
          <Button
            endIcon="refresh"
            color="tomato"
            onClick={onErrorRetry}
            invisible
          >
            Retry
          </Button>
        )}
      </Box>
    </div>
  )
}
